/**
 * Shared barcode parsing and badge detection utilities.
 * Used by AddRawMaterialScannerModal, TransferVerification, and badge scan components.
 */

// GS1-128 AI lookup table: { prefix, aiLen, dataLen } where dataLen = -1 means variable-length
// Matching order: 4-char AIs first (prevents e.g. "3201" matching "01"), then 2-char
const GS1_AI_TABLE: { prefix: string; aiLen: number; dataLen: number }[] = [
  // 4-digit weight AIs: 310n-369n (6-digit data each)
  { prefix: '31', aiLen: 4, dataLen: 6 },
  { prefix: '32', aiLen: 4, dataLen: 6 },
  { prefix: '33', aiLen: 4, dataLen: 6 },
  { prefix: '34', aiLen: 4, dataLen: 6 },
  { prefix: '35', aiLen: 4, dataLen: 6 },
  { prefix: '36', aiLen: 4, dataLen: 6 },
  // Fixed-length 2-digit AIs
  { prefix: '00', aiLen: 2, dataLen: 18 },
  { prefix: '01', aiLen: 2, dataLen: 14 },
  { prefix: '02', aiLen: 2, dataLen: 14 },
  { prefix: '11', aiLen: 2, dataLen: 6 },
  { prefix: '12', aiLen: 2, dataLen: 6 },
  { prefix: '13', aiLen: 2, dataLen: 6 },
  { prefix: '15', aiLen: 2, dataLen: 6 },
  { prefix: '17', aiLen: 2, dataLen: 6 },
  { prefix: '20', aiLen: 2, dataLen: 2 },
  // Variable-length 2-digit AIs
  { prefix: '10', aiLen: 2, dataLen: -1 },
  { prefix: '21', aiLen: 2, dataLen: -1 },
  { prefix: '22', aiLen: 2, dataLen: -1 },
  { prefix: '30', aiLen: 2, dataLen: -1 },
  { prefix: '37', aiLen: 2, dataLen: -1 },
];

/** Find matching AI entry at current position */
function matchAI(barcode: string, pos: number): { prefix: string; aiLen: number; dataLen: number } | null {
  for (const ai of GS1_AI_TABLE) {
    if (barcode.substring(pos, pos + ai.prefix.length) === ai.prefix) {
      return ai;
    }
  }
  return null;
}

export interface ParsedGS1128Barcode {
  gtin?: string;
  weight?: number;
  lot?: string;
  weightAI?: string;
  weightUnit?: 'kg' | 'lb';
  normalizedWeightLb?: number;
}

const KG_TO_LB = 2.20462;

/**
 * Parse GS1-128 barcode to extract GTIN, weight, and lot number.
 * Uses AI lookup table to correctly skip unknown AIs (e.g. 3201, 11, 21)
 * so their digits don't get falsely matched as GTIN or other fields.
 */
export function parseGS1128Barcode(barcode: string): ParsedGS1128Barcode | null {
  const result: ParsedGS1128Barcode = {};

  // Strip RS and parentheses; keep GS (\x1D) as variable-length field delimiter
  const cleanBarcode = barcode.replace(/[\x1E()]/g, '');

  let pos = 0;
  while (pos < cleanBarcode.length) {
    // Skip GS delimiters between fields
    if (cleanBarcode[pos] === '\x1D') { pos++; continue; }

    const ai = matchAI(cleanBarcode, pos);
    if (!ai) {
      pos++;
      continue;
    }

    const aiCode = cleanBarcode.substring(pos, pos + ai.aiLen);
    pos += ai.aiLen;

    // Variable-length: read until GS delimiter or end-of-string
    // Fixed-length: read exact number of characters
    let data: string;
    if (ai.dataLen === -1) {
      const gsIdx = cleanBarcode.indexOf('\x1D', pos);
      const end = gsIdx === -1 ? cleanBarcode.length : gsIdx;
      data = cleanBarcode.substring(pos, end);
      pos = end;
    } else {
      data = cleanBarcode.substring(pos, pos + ai.dataLen);
      pos += ai.dataLen;
    }

    // Extract only the fields we care about
    if (aiCode === '01') {
      result.gtin = data;
    } else if (aiCode.length === 4 && /^3[1-6]/.test(aiCode)) {
      // Weight AI: 4th digit of AI is decimal places
      const decimalPlaces = parseInt(aiCode[3], 10);
      const parsedWeight = parseInt(data, 10) / Math.pow(10, decimalPlaces);
      result.weight = parsedWeight;
      result.weightAI = aiCode;

      if (aiCode.startsWith('31')) {
        result.weightUnit = 'kg';
        result.normalizedWeightLb = parsedWeight * KG_TO_LB;
      } else if (aiCode.startsWith('32')) {
        result.weightUnit = 'lb';
        result.normalizedWeightLb = parsedWeight;
      }
    } else if (aiCode === '10') {
      result.lot = data;
    }
    // All other AIs are correctly skipped
  }

  if (!result.gtin) {
    return null;
  }

  return result;
}

/**
 * Check if a barcode is a badge scan (starts with @+, @i, or @).
 */
export function isBadgeCode(barcode: string): boolean {
  return barcode.startsWith('@+') || barcode.startsWith('@i') || barcode.startsWith('@');
}

/**
 * Extract the raw employee number from a badge code by removing prefix.
 */
export function extractEmployeeNumber(barcode: string): string {
  if (barcode.startsWith('@+')) return barcode.slice(2);
  if (barcode.startsWith('@i')) return barcode.slice(2);
  if (barcode.startsWith('@')) return barcode.slice(1);
  return barcode;
}

/**
 * Normalize any badge format to @+ prefix (the standard format).
 */
export function normalizeBadgeCode(barcode: string): string {
  if (barcode.startsWith('@+')) return barcode;
  if (barcode.startsWith('@i')) return '@+' + barcode.slice(2);
  if (barcode.startsWith('@')) return '@+' + barcode.slice(1);
  return '@+' + barcode;
}

/**
 * Format a raw GS1-128 barcode string into human-readable format with AI parentheses.
 * Uses the same AI lookup table as parseGS1128Barcode for consistent recognition.
 * e.g., "014675114299042932020026001074HBP22b00130" -> "(01)46751142990429(3202)002600(10)74HBP22b(21)00130"
 * Returns '-' for null/undefined/empty values.
 */
export function formatGS1Barcode(value: string | null | undefined): string {
  if (!value) return '-';
  const result: string[] = [];
  let pos = 0;

  while (pos < value.length) {
    // Skip GS delimiters between fields
    if (value[pos] === '\x1D') { pos++; continue; }

    const ai = matchAI(value, pos);
    if (!ai) {
      // No recognized AI at this position — return raw value
      return value;
    }

    const aiCode = value.substring(pos, pos + ai.aiLen);
    pos += ai.aiLen;

    let data: string;
    if (ai.dataLen === -1) {
      const gsIdx = value.indexOf('\x1D', pos);
      const end = gsIdx === -1 ? value.length : gsIdx;
      data = value.substring(pos, end);
      pos = end;
    } else {
      data = value.substring(pos, pos + ai.dataLen);
      pos += ai.dataLen;
    }

    result.push(`(${aiCode})${data}`);
  }

  return result.join('');
}
